import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Mountain, Factory, Filter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const AboutWaterPage = () => {
    const { t, language } = useLanguage();
    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

    const rotatingQuotes = [
        "Nature did the filtering. We did the sourcing.",
        "Pressure made it perfect.",
        "Minerals matter. The rest is just liquid.",
        "Not all water earns its minerals.",
        "More than hydration. It's composition.",
        "Respect the source.",
        "Real minerals. Real difference.",
        "Not just pure. Right."
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentQuoteIndex((prev) => (prev + 1) % rotatingQuotes.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [rotatingQuotes.length]);

    const waterTypes = [
        {
            icon: Factory,
            title: language === 'no' ? 'Springvann' : 'Tap Water',
            description: language === 'no' 
                ? 'Springvann er vann som leveres gjennom det offentlige vannforsyningsnettet, og kommer vanligvis fra innsjøer, elver eller grunnvann. Før det når kranen, gjennomgår det en behandlingsprosess for å sikre at det er trygt å drikke.'
                : 'Tap water is water delivered through the public water supply network, usually sourced from lakes, rivers or groundwater. Before it reaches your tap, it undergoes a treatment process to ensure it is safe to drink.',
            details: language === 'no'
                ? [
                    'Norsk springvann kommer hovedsakelig fra overflatevann som har kort kontakttid med berggrunn, noe som gir lavt mineralinnhold.',
                    'Vannet behandles med klor, UV-lys og filtrering for å fjerne bakterier og forurensninger – en nødvendig prosess for folkehelsen.',
                    'Behandlingen gjør vannet trygt å drikke, men tilfører ingen mineraler. Det du får er rent H2O, uten de naturlige mineralene kroppen trenger.',
                    'Norsk springvann har et velfortjent rykte for å være rent og av høy kvalitet. Samtidig er det viktig å skille mellom vannkvalitet og mineralinnhold. Den norske berggrunnen gir oss vann som ofte er svært mykt – naturlig fattig på mineraler som kalsium og magnesium.'
                ]
                : [
                    'Norwegian tap water mainly comes from surface water that has short contact time with bedrock, resulting in low mineral content.',
                    'The water is treated with chlorine, UV light and filtration to remove bacteria and contaminants – a necessary process for public health.',
                    'Treatment makes the water safe to drink, but adds no minerals. What you get is pure H2O, without the natural minerals your body needs.',
                    'Norwegian tap water has a well-deserved reputation for being clean and of high quality. At the same time, it is important to distinguish between water quality and mineral content. Norwegian bedrock gives us water that is often very soft – naturally low in minerals like calcium and magnesium.'
                ]
        },
        {
            icon: Mountain,
            title: language === 'no' ? 'Kildevann' : 'Spring Water',
            description: language === 'no'
                ? 'Kildevann kommer fra en naturlig underjordisk kilde og tappes direkte ved kilden. Det høres naturlig og rent ut – men det er viktig å forstå hva kildevann faktisk er, og hva det ikke er.'
                : 'Spring water comes from a natural underground source and is bottled directly at the spring. It sounds natural and pure – but it\'s important to understand what spring water actually is, and what it is not.',
            details: language === 'no'
                ? [
                    'Kildevann trenger ikke inneholde spesielle mineraler. Det eneste kravet er at det kommer fra en naturlig kilde.',
                    'Mineralinnholdet i kildevann varierer enormt – fra nesten ingenting til moderate nivåer, avhengig av geologien der kilden ligger.',
                    'Kildevann er ikke det samme som mineralvann. Mens mineralvann har strenge krav til dokumentert og stabilt mineralinnhold, har kildevann ingen slike krav.',
                    'Mange forbrukere tror de får mineraler når de kjøper kildevann, men dette er ikke nødvendigvis tilfellet. Les alltid etiketten for å se det faktiske mineralinnholdet.'
                ]
                : [
                    'Spring water does not need to contain any special minerals. The only requirement is that it comes from a natural source.',
                    'The mineral content in spring water varies enormously – from almost nothing to moderate levels, depending on the geology where the source is located.',
                    'Spring water is not the same as mineral water. While mineral water has strict requirements for documented and stable mineral content, spring water has no such requirements.',
                    'Many consumers believe they are getting minerals when they buy spring water, but this is not necessarily the case. Always read the label to see the actual mineral content.'
                ]
        },
        {
            icon: Droplets,
            title: language === 'no' ? 'Naturlig mineralvann' : 'Natural Mineral Water',
            description: language === 'no'
                ? 'Ekte naturlig mineralvann er i en helt egen kategori. Det er vann som har reist gjennom jordskorpen i årtier, til og med århundrer, og blitt naturlig beriket med mineraler fra bergartene det har passert.'
                : 'True natural mineral water is in a category of its own. It is water that has traveled through the earth\'s crust for decades, even centuries, and been naturally enriched with minerals from the rocks it has passed through.',
            details: language === 'no'
                ? [
                    'EU-lovgivning krever at naturlig mineralvann har dokumentert og stabilt mineralinnhold fra en beskyttet underjordisk kilde.',
                    'Mineralene er i ionisk form – den formen kroppen lettest absorberer. Dette er fundamentalt forskjellig fra mineraler i tablettform.',
                    'Hvert mineralvann har sin unike "signatur" av mineraler, bestemt av geologien i kildeområdet. Dette gir ulike smaker, munnfølelse og funksjonelle egenskaper.',
                    'Mineralvann kan ikke behandles eller tilsettes noe. Det må tappes slik naturen skapte det, ved kilden.'
                ]
                : [
                    'EU legislation requires that natural mineral water has documented and stable mineral content from a protected underground source.',
                    'The minerals are in ionic form – the form the body absorbs most easily. This is fundamentally different from minerals in tablet form.',
                    'Each mineral water has its unique "signature" of minerals, determined by the geology of the source area. This gives different tastes, mouthfeel and functional properties.',
                    'Mineral water cannot be treated or have anything added. It must be bottled as nature created it, at the source.'
                ]
        },
        {
            icon: Filter,
            title: language === 'no' ? 'Filtrert vann' : 'Filtered Water',
            description: language === 'no'
                ? 'Vannfiltrering er spesielt viktig der vannkvaliteten er usikker eller forurenset. Men det er viktig å forstå hva filtrering gjør, og hva konsekvensene kan være ved å drikke helt renset vann over tid.'
                : 'Water filtration is especially important where water quality is uncertain or contaminated. But it\'s important to understand what filtration does, and what the consequences may be of drinking completely purified water over time.',
            details: language === 'no'
                ? [
                    'Filtrering fjerner effektivt forurensninger, tungmetaller, klor, bakterier og andre uønskede stoffer. I områder med dårlig vannkvalitet er dette essensielt for helsen.',
                    'Aktivt kullfilter forbedrer vannets smak og lukt ved å redusere klor og enkelte organiske forbindelser. Samtidig bevares de fleste naturlige mineralene, noe som gjør denne filtreringstypen til et godt kompromiss mellom vannkvalitet og mineralinnhold.',
                    'Omvendt osmose (RO) og destillering produserer vann med en svært høy renhetsgrad ved å fjerne nesten alle oppløste stoffer. Prosessen eliminerer både uønskede forurensninger og de naturlige mineralene som finnes i vannkilden. Resultatet er et vann som i all hovedsak består av H₂O, med et svært lavt mineralinnhold.',
                    'WHO har uttrykt bekymring for langvarig inntak av demineralisert vann. Studier antyder at mineralfattig vann kan påvirke elektrolyttbalansen negativt. Bruk gjerne filter der det trengs, men vær bevisst på at helt renset vann mangler det kroppen trenger.[WHO_LINK]'
                ]
                : [
                    'Filtration effectively removes contaminants, heavy metals, chlorine, bacteria and other unwanted substances. In areas with poor water quality, this is essential for health.',
                    'Activated carbon filters improve water taste and odor by reducing chlorine and certain organic compounds. At the same time, most natural minerals are preserved, making this type of filtration a good compromise between water quality and mineral content.',
                    'Reverse osmosis (RO) and distillation produce water with a very high purity level by removing almost all dissolved substances. The process eliminates both unwanted contaminants and the natural minerals found in the water source. The result is water that essentially consists of H₂O, with very low mineral content.',
                    'WHO has expressed concern about long-term intake of demineralized water. Studies suggest mineral-poor water can negatively affect electrolyte balance. Use filters where needed, but be aware that completely purified water lacks what the body needs.[WHO_LINK]'
                ]
        }
    ];

    return (
        <div className="page-transition" data-testid="about-water-page">
            <SEO 
                title={language === 'no' ? 'Om vann' : 'About Water'}
                description={language === 'no' 
                    ? 'Forstå forskjellen mellom springvann, kildevann, mineralvann og filtrert vann. Lær hvorfor mineralinnhold betyr noe for kroppen din.'
                    : 'Understand the difference between tap water, spring water, mineral water and filtered water. Learn why mineral content matters for your body.'}
                canonical="/about-water"
            />
            
            {/* Hero */}
            <section className="py-20 md:py-32 bg-stone-50">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <p className="caption mb-4">
                            {language === 'no' ? 'Om vann' : 'About Water'}
                        </p>
                        <h1 className="font-display text-5xl md:text-7xl font-light mb-8">
                            {language === 'no' ? 'Ikke alt vann er likt' : 'Not all water is equal'}
                        </h1>
                        <p className="text-lg text-stone-600 leading-relaxed">
                            {language === 'no' 
                                ? 'Vann er grunnlaget for alt liv, men ikke alt vann er sammensatt på samme måte. Ulike vanntyper inneholder ulike mengder mineraler og har forskjellige egenskaper. Ved å forstå forskjellene kan du ta mer bevisste valg for helse, smak og hydrering.'
                                : 'Water is the foundation of all life, but not all water is composed the same way. Different water types contain different amounts of minerals and have different properties. By understanding the differences, you can make more conscious choices for health, taste and hydration.'}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Water Types */}
            {waterTypes.map((waterType, index) => (
                <section 
                    key={waterType.title}
                    className={`py-24 md:py-32 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
                >
                    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <waterType.icon className="w-8 h-8 text-fjord" />
                                <h2 className="font-display text-3xl md:text-4xl font-light">
                                    {waterType.title}
                                </h2>
                            </div>
                            
                            <p className="text-lg text-stone-700 leading-relaxed mb-8 max-w-3xl">
                                {waterType.description}
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {waterType.details.map((detail, detailIndex) => {
                                    // Check if this detail contains the WHO link placeholder
                                    const hasWhoLink = detail.includes('[WHO_LINK]');
                                    const textWithoutPlaceholder = detail.replace('[WHO_LINK]', '');
                                    
                                    return (
                                        <motion.div
                                            key={detailIndex}
                                            initial={{ opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4, delay: detailIndex * 0.1 }}
                                            viewport={{ once: true }}
                                            className="bg-white border border-stone-200 p-6"
                                        >
                                            <p className="text-stone-600 leading-relaxed">
                                                {textWithoutPlaceholder}
                                                {hasWhoLink && (
                                                    <>
                                                        {' '}
                                                        <a 
                                                            href="https://www.who.int/publications/i/item/9241593989"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-fjord underline hover:text-fjord/80 transition-colors"
                                                        >
                                                            {language === 'no' ? 'Les WHO-rapporten' : 'Read the WHO report'}
                                                        </a>
                                                    </>
                                                )}
                                            </p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </div>
                </section>
            ))}

            {/* Philosophy Section with Rotating Quotes */}
            <section className="py-32 bg-fjord text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-8">
                            {t('ourPhilosophy')}
                        </p>
                        <div className="h-28 md:h-24 flex items-center justify-center mb-10">
                            <AnimatePresence mode="wait">
                                <motion.h2
                                    key={currentQuoteIndex}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    className="font-display text-3xl md:text-5xl font-light italic"
                                >
                                    "{rotatingQuotes[currentQuoteIndex]}"
                                </motion.h2>
                            </AnimatePresence>
                        </div>
                        <Link to="/products">
                            <Button 
                                className="bg-white text-fjord hover:bg-white/90 rounded-none px-10 py-6 text-xs uppercase tracking-[0.2em] font-bold"
                            >
                                {t('shopCollection')}
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default AboutWaterPage;
